import React, { useEffect } from "react";
/** https://developer.paypal.com/sdk/js/ */
import {
  PayPalScriptProvider,
  PayPalButtons,
  usePayPalScriptReducer
} from "@paypal/react-paypal-js";
import LoadingView from "presentational/common/LoadingView";
import config from "lib/fr-config";
import GaHelper from "lib/gaHelper";
import { withStore } from "store/storeHandler";
import PropTypes from "prop-types";

const paypalConfig = config.paypal;

PaypalSubscribeBtnView.propTypes = {
  paypalPlanId: PropTypes.string.isRequired,
  onSubscribeViaPaypal: PropTypes.func.isRequired,
  isLatestMembershipInfo: PropTypes.func.isRequired,
  resetButtonType: PropTypes.func.isRequired
};

const btnStyle = {
  layout: "vertical",
  color: "gold",
  shape: "rect",
  height: 40, // 우리 버튼 높이가 40.15정도 되기에 하드코딩.
  label: "subscribe"
};

const sendGAEvent = eventType => {
  switch (eventType) {
    case "buttonInit":
      GaHelper.setGAEvent({
        type: "ma_view_membership_start_btn"
      });
      return null;
    case "buttonClick":
      GaHelper.setGAClickEvent({ type: "membershipStartBtn" });
      return null;

    default:
      console.error(`wrong eventType : "${eventType}"`);
      return null;
  }
};

const ButtonWrapper = ({
  type,
  paypalPlanId,
  onSubscribeViaPaypal,
  isLatestMembershipInfo,
  resetButtonType,
  AppErrorActions
}) => {
  const [{ options, isPending }, dispatch] = usePayPalScriptReducer();

  useEffect(() => {
    /**
     * <ButtonWrapper />상위의 options값이 변경될 경우 리랜더링 위해
     */
    dispatch({
      type: "resetOptions",
      value: {
        ...options,
        intent: "subscription"
      }
    });
  }, [type]);

  const createSubscription = async (data, actions) => {
    if (!(await isLatestMembershipInfo())) {
      resetButtonType();
      return null;
    }

    sendGAEvent("buttonClick");

    try {
      return actions.subscription.create({
        plan_id: paypalPlanId,
        application_context: {
          brand_name: "Maunent",
          /** 하단 user_action 옵션 CONTINUE 아닌 SUBSCRIBE_NOW 설정시 백엔드에서 db작성전 ACTIVE 상태되므로 문제발생!!!! */
          user_action: "CONTINUE",
          shipping_preference: "NO_SHIPPING",
          payment_method: {
            payer_selected: "PAYPAL",
            payee_preferred: "IMMEDIATE_PAYMENT_REQUIRED"
          }
        }
      });
    } catch (error) {
      AppErrorActions.setSafeAppError(error);
    }
  };

  const onApprove = async (data, actions) => {
    await onSubscribeViaPaypal(data.subscriptionID);
  };

  const onError = error => {
    const { message } = error;
    const errObj = Error(message);
    /**
     * Component closed : 페이팔 결제창 X버튼 클릭으로 종료시 발생.
     * Detected popup close : 팝업창 내 화면 완성전 로딩중에 팝업창 윈도우의 X버튼 클릭하여 제거시 발생.
     */
    if (message === "Component closed" || message === "Detected popup close") {
      return null;
    }
    AppErrorActions.setSafeAppError(errObj);
  };

  if (isPending) {
    return (
      <div style={{ paddingTop: 20 }}>
        <LoadingView />
      </div>
    );
  } else {
    return (
      <PayPalButtons
        style={btnStyle}
        createSubscription={createSubscription}
        onApprove={onApprove}
        onError={onError}
      />
    );
  }
};

function PaypalSubscribeBtnView(props) {
  const {
    paypalPlanId,
    onSubscribeViaPaypal,
    isLatestMembershipInfo,
    resetButtonType,
    AppErrorActions
  } = props;

  useEffect(() => {
    sendGAEvent("buttonInit");
  }, []);

  return (
    <PayPalScriptProvider
      options={{
        "client-id": paypalConfig.client_id,
        components: "buttons",
        intent: "subscription",
        vault: true
      }}
    >
      <ButtonWrapper
        type="subscription"
        paypalPlanId={paypalPlanId}
        onSubscribeViaPaypal={onSubscribeViaPaypal}
        isLatestMembershipInfo={isLatestMembershipInfo}
        resetButtonType={resetButtonType}
        AppErrorActions={AppErrorActions}
      />
    </PayPalScriptProvider>
  );
}

export default withStore({
  dispatchMap: ["appError"]
})(PaypalSubscribeBtnView);
